/**
 * Monday theme → Vibe theme class.
 *
 * ONE signal, and only one. The app's colours must follow the monday account's
 * theme, which arrives as `context.theme` on the SDK's `context` event. Nothing
 * else gets a vote — in particular NOT the viewer's operating-system setting.
 *
 * The removed `@media (prefers-color-scheme: dark)` block in App.css was a
 * second, contradictory signal: it forced Vibe's radio labels and inputs to
 * #323338 whenever the OS was dark, regardless of the board's theme. That was
 * added (82f53b6, "change theme color for text in darkmode") as a band-aid for
 * the hardcoded `bg-white` cards — dark-themed Vibe text was invisible on them,
 * and a dark OS happened to correlate with a dark monday theme often enough to
 * look like a fix. It also broke the honest case: on a genuinely dark surface,
 * such as a Vibe Modal, it painted dark ink onto a dark ground, which is what
 * made the "Resolve columns" radio labels unreadable.
 *
 * Vibe declares its light palette on `.light-app-theme` as well as `:root`, so
 * we set a class for EVERY theme including light, rather than toggling one off.
 * That keeps the resolved theme visible in the DOM and stops the app depending
 * on `:root` fallthrough.
 */

/** Vibe's four theme classes. Anything we add, we must be able to remove. */
export const THEME_CLASSES = [
  'light-app-theme',
  'dark-app-theme',
  'black-app-theme',
  'hacker_theme-app-theme',
];

/**
 * monday `context.theme` values → Vibe class. The key IS the theme string
 * monday sends; the value is Vibe's `${theme}-app-theme` class, which exists
 * in monday-ui-react-core/dist/main.css for all four.
 */
const THEME_CLASS_BY_NAME = {
  light: 'light-app-theme',
  dark: 'dark-app-theme',
  black: 'black-app-theme',
  hacker_theme: 'hacker_theme-app-theme',
};

export const DEFAULT_THEME_CLASS = 'light-app-theme';

/**
 * Resolve a monday theme name to its Vibe class, falling back to light.
 *
 * Falling back rather than passing the name through matters: an unknown theme
 * would otherwise produce a class Vibe has no rules for, leaving every token
 * at its `:root` light value on a ground monday paints dark — the exact
 * unreadable state this whole change exists to remove. Light is the safe
 * default because Vibe's `:root` already holds the light palette, so the app
 * stays internally consistent even when it guesses the ground wrong.
 */
export function themeClassFor(theme) {
  return THEME_CLASS_BY_NAME[theme] || DEFAULT_THEME_CLASS;
}

/**
 * Put exactly one theme class on `el` (default <body>) and return it.
 *
 * Applied to <body> rather than the React root because Vibe portals its Modal,
 * Dialog and Tooltip content to direct children of <body> — a class on #root
 * would leave every popover unthemed.
 */
export function applyMondayTheme(theme, el) {
  const target = el || document.body;
  const next = themeClassFor(theme);

  THEME_CLASSES.forEach((cls) => {
    if (cls !== next) target.classList.remove(cls);
  });
  target.classList.add(next);

  return next;
}
